import {
  ProductDetails,
  ProductQuantity,
  ProductSlider,
} from '@components/product'
import ProductPaymentOptions from '@components/product/ProductPaymentOptions'
import SizeSelector, { SelectOption } from '@components/product/SizeSelector'
import { Button, Container, useUI } from '@components/ui'
import { useAddItem } from '@framework/cart'

import { addToCartEvent } from '@lib/events/addToCart'
import { getProductSizesAndVariantDetails } from '@lib/get-product-size'
import { getProductVariant } from '@lib/get-product-variant'
import { fetcher } from '@lib/helpers/fetcher'
import useYup from '@lib/hooks/useYup'
import { useStoreSidebar } from '@lib/store-finder-sidebar'
import cn from 'clsx'
import { NextSeo } from 'next-seo'
import Image from 'next/legacy/image'
import { useRouter } from 'next/router'
import { PhotoSwipeOptions } from 'photoswipe'
import { FC, useEffect, useMemo, useState } from 'react'
import { Gallery, GalleryProps } from 'react-photoswipe-gallery'
import {
  DownloadBlock,
  ImagesBlock,
  ProductInfoBlocks,
  ProductInfoListing,
  TextBlock,
  TextTable,
} from 'slices'
import useSWR from 'swr'
import { BreadcrumItem } from 'types/Common'
import type { Product } from 'types/Product'
import * as Yup from 'yup'
import FulfilmentWidget from '../FulfilmentWidget'
import ProductDiscountMessage from '../ProductDiscountMessage'
import ProductSidebar from '../ProductSidebar'
import WaterResistance from '../WaterResistance'
import { useTagPromoOff } from '@lib/TagPromoOffProvider'
import getNamedTag from '@lib/get-named-tag'
import PromotionWidget from '@components/promotions/PromotionWidget'
import generateProductSchema from '@lib/generate-product-schema'
import s from './ProductView.module.css'

interface Props {
  children?: any
  product: Product.Product
  className?: string
  productInfoDocs: any | null
  pdpSpecificInfoDocument: any | null
  breadCrumbOptions?: BreadcrumItem[]
  sizeChartData?: any | null
  waterResistanceData?: any | null
  promotionConfig?: any
  promoProduct?: any
  giftWithPurchaseData?: any
  giftWithPurchaseConfig?: any
  defaultVariant: Product.ProductVariant
}

export type ProductSize = { label: string; value: string | number }

const defaultImages = [
  {
    url: '/product-img-placeholder.svg',
    alt: 'Hoskings Jewellers',
  },
]

const toTitleCase = (text: string) => {
  return text.replace(/\w\S*/g, function (string: string) {
    return string.charAt(0).toUpperCase() + string.substr(1).toLowerCase()
  })
}

const ProductView: FC<Props> = ({
  product,
  productInfoDocs,
  pdpSpecificInfoDocument,
  sizeChartData,
  waterResistanceData,
  promoProduct,
  defaultVariant,
}) => {
  const router = useRouter()
  const addItem = useAddItem()
  const { openSidebar, setSidebarView } = useUI()
  const [sidebarData, setSidebarData] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [quantity, setQuantity] = useState(1)
  const [selectedVariant, setSelectedVariant] = useState(defaultVariant)

  // Gets changed by link navigation but doesn't update state without this.
  useEffect(() => setSelectedVariant(defaultVariant), [defaultVariant])

  useEffect(() => {
    if (router.query?.variant) {
      const selectedDefaultVariantCandidate = product.variants.find(
        (variant: Product.ProductVariant) =>
          variant.id ===
          `gid://shopify/ProductVariant/${router.query?.variant as string}`
      )
      if (selectedDefaultVariantCandidate) {
        setSelectedVariant(selectedDefaultVariantCandidate)
      }
    }
  }, [router.query?.variant])

  const productSizeOptions = getProductSizesAndVariantDetails(product, [
    'quantityAvailable',
  ])

  const hasProductSizeOptions = productSizeOptions.length > 0
  const selectedProductSizeOption = productSizeOptions.find(
    // @ts-ignore
    (option) => option.label === selectedVariant.name
  )
  const variantOptionDisplayName = product?.options[0]?.displayName
  const variantTitle = (displayName: string) => {
    switch (displayName) {
      case 'choose your initial':
        return "Choose Your Initial"
      case 'denominations':
        return 'gift card value'
      default:
        return `Select ${displayName}`
    }
  }
  const [addCartItem, prepareAddCart] = useState({
    quantity,
    ...(hasProductSizeOptions && {
      option: selectedProductSizeOption,
    }),
  })

  const addCartSchemaValidation = useMemo(() => {
    const objs = {
      quantity: Yup.number()
        .min(1)
        .required()
        .typeError('Please select a quantity'),
      ...(hasProductSizeOptions && {
        option: Yup.object()
          .shape({
            label: Yup.string(),
            value: Yup.string(),
          })
          .required()
          .typeError(
            `Please select a ${variantTitle(variantOptionDisplayName)}`
          ),
      }),
    }
    return Yup.object(objs)
  }, [hasProductSizeOptions])

  const { errors: addCartItemErrors } = useYup(
    addCartItem,
    addCartSchemaValidation
  )

  // Values for validation
  useEffect(() => {
    prepareAddCart((prev) => ({
      ...prev,
      quantity,
      ...(hasProductSizeOptions && {
        option: selectedProductSizeOption,
      }),
    }))

    if ((window as any)._learnq === undefined) return

    const KlaviyoItem = {
      $value: selectedVariant?.price || product?.price?.value,
      Name: product?.name,
      Handle: product?.slug,
      ID: selectedVariant?.id || product?.id,
      SKU: selectedVariant?.sku,
      Categories: product?.productType,
      ImageURL: images[0]?.url,
      URL: router.asPath,
      Brand: brand,
      Price: selectedVariant?.price || product?.price?.value,
      CompareAtPrice: selectedVariant?.listPrice,
      Quantity: quantity,
      Metadata: {
        Brand: brand,
        Price: selectedVariant?.price || product?.price?.value,
        CompareAtPrice: selectedVariant?.listPrice,
      },
    }

    const handleViewedProduct = () => {
      ;(window as any)._learnq.push(['trackViewedItem', KlaviyoItem])
    }
    window.addEventListener('ViewProduct', handleViewedProduct)
    window.dispatchEvent(new Event('ViewProduct'))
    return () => window.removeEventListener('ViewProduct', handleViewedProduct)
  }, [product, quantity, selectedVariant])

  const handleSelectProductSize = (selectedSizeId: ProductSize['value']) => {
    if (!selectedSizeId) return

    const selectedOption = productSizeOptions.find(
      (option) => option.value === selectedSizeId
    )

    if (!selectedOption) return setSelectedVariant(defaultVariant)

    const findVariantFromSelectedOption = product.variants.find(
      (variant) => variant.id === selectedOption.value
    )
    if (!findVariantFromSelectedOption) return
    setSelectedVariant(findVariantFromSelectedOption)
  }

  const shouldAddToCart = useMemo(() => {
    return loading || addCartItemErrors
  }, [loading, addCartItemErrors])

  const addCartItemErrorMessage = useMemo(() => {
    return addCartItemErrors && Object.values(addCartItemErrors)[0]
  }, [addCartItemErrors])

  const handleQuantity = (number: number) => {
    setQuantity(number)
  }

  const addToCart = async () => {
    setLoading(true)

    addToCartEvent(product, selectedVariant, quantity)

    try {
      await addItem({
        productId: String(product.id),
        variantId: String(
          selectedVariant ? selectedVariant.id : product.variants[0].id
        ),
        quantity: quantity,
      })
      setSidebarView('CART_VIEW')
      openSidebar()
      setLoading(false)
    } catch (err) {
      setLoading(false)
    }
  }

  let promoImages

  if (promoProduct.length > 0) {
    promoImages = {
      url: promoProduct[1].product.images[0].url,
      altText: promoProduct[1].product.images[0].altText,
      width: promoProduct[1].product.images[0].width,
      height: promoProduct[1].product.images[0].height,
    }
  }

  const images = product.images.length === 0 ? defaultImages : product.images

  const productAvailable = product.variants.filter(
    (item) => item.quantityAvailable > 0
  )

  const addingToCart = () => {
    if (!!shouldAddToCart) {
      return addCartItemErrorMessage
    } else if (
      quantity > selectedVariant?.quantityAvailable &&
      selectedVariant?.quantityAvailable > 0
    ) {
      return `Only ${selectedVariant?.quantityAvailable} available`
    } else if (selectedVariant?.quantityAvailable > 0) {
      return 'Add to Cart'
    } else {
      return 'Sold out'
    }
  }

  let brand =
    getNamedTag(product.tags as string[], 'Brand')?.replace('_', ' ') ??
    'Hoskings Jewellers'

  const { withTagPromoOff } = useTagPromoOff()

  const tags: string[] = product.tags ? product.tags : []

  const {
    price,
    compare_at_price: listPrice,
    isPromoOff,
    isOnSale,
  } = useMemo(
    () =>
      withTagPromoOff(tags, selectedVariant?.price, selectedVariant?.listPrice),
    [tags, selectedVariant, withTagPromoOff]
  )

  const isPremium = tags.map((tag) => tag.toLowerCase()).includes('premium:yes')

  brand = toTitleCase(brand)

  // for product Generic Information
  const productInfoList = productInfoDocs.find(
    (slice: any) => slice.slice_type === 'product_info_listing'
  )
  const productInfoBlocks = productInfoDocs.find(
    (slice: any) => slice.slice_type === 'product_info_blocks'
  )

  // for product Individual Information
  const productImageBlock = pdpSpecificInfoDocument.find(
    (slice: any) => slice.slice_type === 'images_block'
  )
  const productTextBlock = pdpSpecificInfoDocument.find(
    (slice: any) => slice.slice_type === 'text_block'
  )
  const productTextTable = pdpSpecificInfoDocument.find(
    (slice: any) => slice.slice_type === 'text_table'
  )
  // const productModal = pdpSpecificInfoDocument.find(
  //   (slice: any) => slice.slice_type === 'modal'
  // )
  const productDownloadBlock = pdpSpecificInfoDocument.find(
    (slice: any) => slice.slice_type === 'download_block'
  )

  let waterResistanceType: any = null

  if (product) {
    const waterResistanceTypeTag: any = product?.tags?.find((tag: string) =>
      tag.startsWith('water-resistance:')
    )
    waterResistanceType = waterResistanceTypeTag?.replace(
      'water-resistance:',
      ''
    )
  }

  const openSizeChart = () => {
    if (sizeChartData) {
      setSidebarData({
        type: 'sizechart',
        ...sizeChartData,
      })
    }
  }
  const openWaterResistance = () => {
    if (waterResistanceData) {
      setSidebarData({
        type: 'water-resistance',
        ...waterResistanceData,
      })
    }
  }

  const imageQuantity = product.images.length
  const closeSVGString =
    '<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">\n' +
    '<circle cx="16" cy="16" r="16" fill="#F6F5F4"/>\n' +
    '<path d="M11.8751 11.2861C11.7124 11.1234 11.4486 11.1234 11.2859 11.2861C11.1231 11.4488 11.1231 11.7126 11.2859 11.8754L15.4106 16.0001L11.2859 20.1249C11.1231 20.2877 11.1231 20.5515 11.2859 20.7142C11.4486 20.8769 11.7124 20.8769 11.8751 20.7142L15.9999 16.5894L20.1247 20.7142C20.2874 20.8769 20.5512 20.8769 20.7139 20.7142C20.8767 20.5515 20.8767 20.2877 20.7139 20.1249L16.5892 16.0001L20.7139 11.8754C20.8767 11.7126 20.8767 11.4488 20.7139 11.2861C20.5512 11.1234 20.2874 11.1234 20.1247 11.2861L15.9999 15.4109L11.8751 11.2861Z" fill="#464544"/>\n' +
    '</svg>\n'
  const leftArrowSVGString =
    '<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">\n' +
    '<circle cx="20" cy="20" r="20" fill="#F6F5F4"/>\n' +
    '<path d="M23.0961 26.1516L23.295 25.9527C23.4267 25.821 23.4267 25.608 23.295 25.4763L17.8175 20.0016L23.295 14.524C23.4267 14.3923 23.4267 14.1794 23.295 14.0477L23.0961 13.8488C22.9644 13.7171 22.7515 13.7171 22.6198 13.8488L16.7051 19.7606C16.5735 19.8923 16.5735 20.1052 16.7051 20.2369L22.6198 26.1488C22.7515 26.2833 22.9644 26.2833 23.0961 26.1516Z" fill="#464544"/>\n' +
    '</g>\n' +
    '</svg>\n'
  const rightArrowSVGString =
    '<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">\n' +
    '<circle cx="20" cy="20" r="20" fill="#F6F5F4"/>\n' +
    '<path d="M16.904 26.1516L16.7051 25.9527C16.5734 25.821 16.5734 25.608 16.7051 25.4763L22.1826 20.0016L16.7051 14.524C16.5734 14.3923 16.5734 14.1794 16.7051 14.0477L16.904 13.8488C17.0357 13.7171 17.2486 13.7171 17.3803 13.8488L23.295 19.7606C23.4267 19.8923 23.4267 20.1052 23.295 20.2369L17.3803 26.1488C17.2486 26.2833 17.0357 26.2833 16.904 26.1516Z" fill="#464544"/>\n' +
    '</svg>\n'
  const options: PhotoSwipeOptions = {
    arrowPrevSVG: leftArrowSVGString,
    arrowNextSVG: rightArrowSVGString,
    closeSVG: closeSVGString,
    showHideAnimationType: 'fade',
    bgOpacity: 1,
    counter: true,
    zoom: false,
    arrowKeys: true,
    mainClass: 'pswp--custom-bg',
    pinchToClose: false,
    closeOnVerticalDrag: false,
    tapAction: false,
    initialZoomLevel: 'fit',
    secondaryZoomLevel: 2,
    maxZoomLevel: 4,
    preload: [imageQuantity, imageQuantity],
  }
  const uiElements: GalleryProps['uiElements'] = [
    {
      name: 'bulletsIndicator',
      className: 'pswp__bullets-indicator',
      appendTo: 'wrapper',
      onInit: (el: any, pswp: any) => {
        const bullets: any = []
        let bullet
        let prevIndex = -1
        for (let i = 0; i < pswp.getNumItems(); i++) {
          bullet = document.createElement('div')
          bullet.className = 'pswp__bullet'
          bullet.style.width = `calc(100% / ${imageQuantity})`
          el.appendChild(bullet)
          bullets.push(bullet)
        }
        pswp.on('change', (a: any) => {
          if (prevIndex >= 0) {
            bullets[prevIndex].classList.remove('pswp__bullet--active')
          }
          bullets[pswp.currIndex].classList.add('pswp__bullet--active')
          prevIndex = pswp.currIndex
        })
      },
    },
  ]

  const priceFormatter = new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
  })

  const namedTags: any = {}
  product.tags
    ?.filter((tag: string) => tag.indexOf(':') > 0)
    .forEach((tag: string) => {
      namedTags[tag.split(':')[0]] = tag.split(':')[1]
    })

  const hasDiscountText = namedTags?.discount_text?.length

  const getGiftCardPriceRange = (
    product: { variants: any },
    variant: { price: any }
  ) => {
    const variantArray = product.variants
    const lastVariantPriceValue = variantArray[variantArray.length - 1].price
    return !selectedProductSizeOption
      ? `${priceFormatter.format(variant?.price)} - ${priceFormatter.format(
          lastVariantPriceValue
        )}`
      : priceFormatter.format(variant?.price)
  }

  const displayPrice = () => {
    if (isPremium) return priceFormatter.format(listPrice || price)
    const mainPrice =
      variantOptionDisplayName === 'denominations'
        ? getGiftCardPriceRange(product, selectedVariant)
        : priceFormatter.format(price)
    const originalPrice =
      listPrice && price && listPrice > price ? (
        <del className="pl-2 text-text-disabled">
          <span className="text-text-disabled">
            {priceFormatter.format(listPrice)}
          </span>
        </del>
      ) : null

    return (
      <>
        {mainPrice}
        {originalPrice}
      </>
    )
  }

  return (
    <Container className="max-w-none wrapper" clean>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            generateProductSchema(product, selectedVariant)
          ),
        }}
      />
      <NextSeo
        title={product.name}
        description={product.description}
        openGraph={{
          type: 'website',
          title: product.name,
          description: product.description,
          images: [
            {
              url: images[0]?.url!,
              width: 800,
              height: 600,
              alt: product.name,
            },
          ],
        }}
      />
      <ProductSidebar
        sidebarData={sidebarData}
        onClose={() => {
          setSidebarData(null)
        }}
      />
      <div className={cn(s.root, 'fit')}>
        <div className={cn(s.productDisplay)}>
          <div className={s.sliderContainer}>
            <Gallery options={options} uiElements={uiElements}>
              <ProductSlider
                key={product.id}
                images={images}
                product={product}
                saleBadge={isOnSale}
                isPromoOff={isPromoOff}
                isPremium={isPremium}
              >
                {images.map((image, i) => (
                  <div key={image.url}>
                    <Image
                      className={s.img}
                      src={image.url!}
                      alt={image.alt || 'Hoskings Jewellers Product Image'}
                      width={720}
                      height={720}
                      priority={i === 0}
                      quality="85"
                      unoptimized
                    />
                  </div>
                ))}
              </ProductSlider>
            </Gallery>
            {/* left-button container */}
            <div className="hidden laptop:flex">
              <div className={s.sliderContainerInformation}>
                <ProductDetails product={product} />
                {productImageBlock && <ImagesBlock slice={productImageBlock} />}
                {productTextBlock && <TextBlock slice={productTextBlock} />}
                {productTextTable && (
                  <TextTable slice={productTextTable} />
                )}{' '}
                <WaterResistance
                  waterResistanceType={waterResistanceType}
                  openWaterResistance={openWaterResistance}
                />
                {productDownloadBlock && (
                  <DownloadBlock slice={productDownloadBlock} />
                )}
              </div>
            </div>
          </div>
        </div>
        <div className={cn(s.sidebar)}>
          <div className="flex flex-col">
            <div className="text-label font-normal pb-3 uppercase">
              {brand.replace(/_/g, ' ')}
            </div>
            <h1 className="text-h4 font-normal pb-3">{product.name}</h1>
            <h4 className="text-label font-normal text-ui-grey-50">
              SKU: {selectedVariant.sku}
            </h4>
          </div>
          {productAvailable?.length === 0 ? (
            <div>Out Of Stock</div>
          ) : (
            <>
              <div
                className={cn(
                  'text-16 leading-7 font-bold inline-block tracking-wide',
                  {
                    'text-text-sale': isOnSale && !isPromoOff && !isPremium,
                    'text-promo-green': isPromoOff,
                  },
                  {
                    'pb-2': !hasDiscountText,
                  }
                )}
              >
                {displayPrice()}
              </div>
              {!isOnSale && <ProductDiscountMessage namedTags={namedTags} />}
              {price && <ProductPaymentOptions price={price} />}
              {hasProductSizeOptions && (
                <div className="mt-4">
                  <SizeSelector
                    label="size"
                    hasSizeGuide={!!sizeChartData}
                    options={productSizeOptions as SelectOption[]}
                    selected={selectedProductSizeOption as SelectOption}
                    onClick={openSizeChart}
                    onChange={(e) => handleSelectProductSize(e.target.value)}
                    onSelectOption={handleSelectProductSize}
                    variantTitle={variantTitle(variantOptionDisplayName)}
                  />
                </div>
              )}
              <div className="flex gap-4 mt-6 mb-3 ">
                <ProductQuantity
                  isLocked={
                    hasProductSizeOptions && !selectedProductSizeOption
                      ? true
                      : false
                  }
                  quantity={quantity}
                  setQuantity={handleQuantity}
                />
                <Button
                  aria-label="Add to Cart"
                  type="button"
                  onClick={addToCart}
                  loading={loading}
                  disabled={
                    !!shouldAddToCart ||
                    selectedVariant?.availableForSale === false ||
                    selectedVariant?.quantityAvailable < 1 ||
                    quantity > selectedVariant?.quantityAvailable
                  }
                  variant="medium"
                  width="100%"
                >
                  {addingToCart()}
                </Button>
              </div>
              {promoProduct[0]?.promotion.freeorDiscountedProduct !=
              product.slug ? (
                <>
                  {promoProduct[0] && (
                    <PromotionWidget
                      product={promoProduct[0]}
                      promotionImages={promoImages}
                    />
                  )}
                </>
              ) : null}
              {productInfoList && (
                <ProductInfoListing slice={productInfoList} />
              )}
            </>
          )}
          <div>
            <FulfilmentWidget
              totalInventoryQuantity={selectedVariant?.quantityAvailable ?? 0}
              shippable={!(product?.tags as string[])?.includes('Weight:cnc')}
            />
            <div className="mobileL:hidden">
              {productInfoBlocks && (
                <ProductInfoBlocks slice={productInfoBlocks} />
              )}
            </div>
          </div>
          {/* left-button container */}
          <div className="flex laptop:hidden">
            <div className={s.sliderContainerInformation}>
              <ProductDetails product={product} />
              {productImageBlock && <ImagesBlock slice={productImageBlock} />}
              {productTextBlock && <TextBlock slice={productTextBlock} />}
              {productTextTable && <TextTable slice={productTextTable} />}
              <WaterResistance
                waterResistanceType={waterResistanceType}
                openWaterResistance={openWaterResistance}
              />
              {productDownloadBlock && (
                <DownloadBlock slice={productDownloadBlock} />
              )}
            </div>
          </div>
          <div className="mobileL:visible">
            {productInfoBlocks && (
              <ProductInfoBlocks slice={productInfoBlocks} />
            )}
          </div>
        </div>
      </div>
      <style jsx global>
        {`
          .pswp {
            --pswp-bg: #ffffff;
            --pswp-placeholder-bg: #ffffff;
            --pswp-root-z-index: 2147483640;
          }
          .pswp__button--arrow--prev {
            width: 45px;
            display: flex;
            align-items: center;
            justify-content: start;
            padding-left: 12px !important;
          }
          .pswp__button--arrow--next {
            width: 45px;
            display: flex;
            align-items: center;
            justify-content: end;
            padding-right: 12px !important;
          }
          .pswp__button--close {
            width: 92px;
            height: 55px;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 0px !important;
            margin: 0 !important;
            position: absolute;
            top: 12px;
            right: 15px;
          }
          .pswp__button--close::before {
            content: 'Close';
            padding-right: 12px;
          }
          .pswp__bullets-indicator {
            width: 90%;
            max-width: 272px;
            height: 4px;
            background: #f4f4f4;
            box-sizing: border-box;
            overflow-y: hidden;
            overflow-x: auto;
            white-space: nowrap;
            scrollbar-width: none;
            margin: auto;
            position: fixed;
            left: 0;
            bottom: 24px;
            right: 0;
            border-radius: 2px;
            display: flex;
          }
          .pswp__bullet {
            height: 100%;
            width: calc(100% / ${imageQuantity});
            border-radius: 2px;
          }
          .pswp__bullet--active {
            background: #474747;
          }
          .pswp--touch .pswp__button--arrow {
            visibility: visible;
          }
          .pswp__counter {
            font-family: 'Lato', serif;
            font-weight: 400;
            font-size: 16px;
            line-height: 28px;
            color: #474747;
            text-shadow: none;
            opacity: unset;
            margin: 26px 0 0 20px;
          }
        `}
      </style>
    </Container>
  )
}

export default ProductView
